
import { Link } from "react-router-dom";
import { useMeToday, useMeMantras } from "@/hooks/use-me-queries";
import { getMeSession } from "@/lib/auth/storage";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AttendanceCellBadge } from "@/components/attendance/attendance-cell";
import { formatDateDisplay, formatTimeIST, todayIST } from "@/lib/timezone";
import { canRaiseForDate } from "@/lib/raise-window";
import { CATEGORY_LABELS, normalizePersonCategories, sessionIsYoga } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { ApiError } from "@/lib/api/client";

const NOTICE_STATUSES = new Set(["A", "L", "pending"]);

export default function MeTodayPage() {
  const session = getMeSession();
  const { data, isLoading, error } = useMeToday();
  const { data: mantras } = useMeMantras();
  const isYoga =
    sessionIsYoga(session) ||
    (data?.categories ?? []).includes("YOGA") ||
    !!mantras?.applies;

  if (!session) {
    return null;
  }

  if (isLoading) {
    return (
      <p className="text-center text-muted-foreground">Loading today…</p>
    );
  }

  if (error) {
    const isNotFound =
      error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-5">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Today</p>
          <h2 className="text-2xl font-semibold">
            {formatDateDisplay(todayIST())}
          </h2>
          <p className="mt-2 text-lg">Namaste, {session.name}</p>
        </div>
        <Card className="border-saffron-100">
          <CardContent className="pt-6 text-center text-sm text-muted-foreground">
            {isNotFound ? (
              <>
                You&apos;re signed in. Today&apos;s attendance endpoint is not
                available on the server yet — check back after your coordinator
                enables <code className="text-xs">/biometric/me/today</code>.
              </>
            ) : (
              <>Could not load today&apos;s status. Please try again shortly.</>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <p className="text-center text-red-600">
        Could not load today&apos;s status.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div className="text-center">
        <p className="text-sm text-muted-foreground">Today</p>
        <h2 className="text-2xl font-semibold">
          {formatDateDisplay(data.date)}
        </h2>
        <div className="mt-2 flex flex-wrap justify-center gap-1">
          {normalizePersonCategories(
            data.categories ?? session.person_categories
          ).map((c) => (
            <Badge key={c} variant="secondary">
              {CATEGORY_LABELS[c] ?? c}
            </Badge>
          ))}
        </div>
      </div>

      {!data.morning_finalized && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center text-sm text-slate-600">
          Morning status finalizes at 7:00 AM IST if you haven&apos;t punched.
        </p>
      )}

      {isYoga && mantras && (mantras.open_count > 0 || mantras.fine_total_rupees > 0) && (
        <Link
          to="/me/mantras"
          className="block rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-950"
        >
          <p className="font-medium">Yoga mahamantras</p>
          {mantras.due.length > 0 && (
            <p>
              Write mahamantras today for {mantras.due.length} day
              {mantras.due.length === 1 ? "" : "s"} — ₹{mantras.fine_rupees} from
              tomorrow if missed.
            </p>
          )}
          {mantras.submitted.length > 0 && (
            <p>
              {mantras.submitted.length} write-up
              {mantras.submitted.length === 1 ? "" : "s"} waiting for
              coordinator confirmation.
            </p>
          )}
          {mantras.fine_total_rupees > 0 && (
            <p className="font-semibold text-red-700">
              Fine so far: ₹{mantras.fine_total_rupees}
            </p>
          )}
          <p className="mt-1 text-xs text-amber-800">Tap to view details</p>
        </Link>
      )}

      <div className="space-y-3">
        {data.sessions.map((sessionRow) => {
          const canApply =
            NOTICE_STATUSES.has(sessionRow.status) && canRaiseForDate(data.date);
          const card = (
            <Card
              className={
                canApply
                  ? "border-saffron-200 transition-colors hover:bg-saffron-50/60"
                  : "border-saffron-100"
              }
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{sessionRow.label}</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-between gap-3">
                <AttendanceCellBadge
                  showWord
                  cell={{
                    status: sessionRow.status,
                    late_minutes: sessionRow.late_minutes,
                    first_punch_at: sessionRow.first_punch_at,
                  }}
                />
                {sessionRow.first_punch_at && (
                  <p className="text-sm text-muted-foreground">
                    {formatTimeIST(sessionRow.first_punch_at)}
                  </p>
                )}
              </CardContent>
              {canApply && (
                <p className="px-6 pb-4 text-xs font-medium text-saffron-800">
                  Tap to apply sick, official, or permission
                </p>
              )}
            </Card>
          );

          if (!canApply) {
            return (
              <div key={`${sessionRow.category ?? ""}-${sessionRow.session}`}>
                {card}
              </div>
            );
          }

          return (
            <Link
              key={`${sessionRow.category ?? ""}-${sessionRow.session}`}
              to={`/me/notice?dates=${data.date}`}
              className="block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-saffron-500"
            >
              {card}
            </Link>
          );
        })}
      </div>

      {data.sessions.length === 0 && (
        <p className="text-center text-muted-foreground">
          No sessions scheduled for you today.
        </p>
      )}
    </div>
  );
}
